import React, { useEffect, useState } from 'react';
import { config } from '../config';
import { mockWeatherData, mockTrafficData } from '../mockData';

interface RouteOption {
  via: string;
  driveTime: number;
  coordinates: any[];
}

interface Destination {
  name: string;
  routes: RouteOption[];
}

interface TrafficAlert {
  id: string;
  type: 'accident' | 'construction' | 'closure' | 'delay' | 'other';
  location: string;
  description: string;
  criticality: string;
  distance: number;
}

// HERE API response types
interface HereIncident {
  location: {
    length: number;
    shape: {
      links: Array<{
        points: Array<{ lat: number; lng: number }>;
        length: number;
        functionalClass: number;
      }>;
    };
  };
  incidentDetails: {
    id: string;
    criticality: string;
    type: string;
    description: { value: string };
    summary: { value: string };
  };
}

interface HereTrafficResponse {
  sourceUpdated: string;
  results: HereIncident[];
}

interface HourlyForecast {
  hour: string;
  temp: number;
  condition: string;
}

interface WeatherData {
  temperature: number;
  condition: string;
  humidity: number;
  windSpeed: number;
  hourly: HourlyForecast[];
}

// Rocket Mortgage FieldHouse coordinates
const STADIUM_LAT = 41.4966;
const STADIUM_LNG = -81.6882;

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; 
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// TEMPORARY: Check if location contains actual road/street names
// Remove this function and related sorting logic when no longer needed
const hasRoadName = (location: string): boolean => {
  const roadPatterns = /\b(St|Ave|Rd|Blvd|Dr|Hwy|Highway|I-\d+|US-\d+|SR-\d+|Route|Pkwy|Parkway|Ln|Lane|Way|Ct|Court|Pl|Place|Cir|Circle|Ter|Terrace|Bridge|Ramp|Exit)\b/i;
  return roadPatterns.test(location);
};

// Map Open-Meteo weather codes to condition strings
const getWeatherCondition = (code: number): string => {
  if (code === 0) return 'Clear';
  if (code <= 3) return 'Partly Cloudy';
  if (code <= 49) return 'Foggy';
  if (code <= 59) return 'Drizzle';
  if (code <= 69) return 'Rain';
  if (code <= 79) return 'Snow';
  if (code <= 99) return 'Thunderstorm';
  return 'Unknown';
};

// Get weather icon image path for condition
const getWeatherIcon = (condition: string): string => {
  switch (condition) {
    case 'Clear': return '/weather-icons/sunny.png';
    case 'Partly Cloudy': return '/weather-icons/partly_sunny.png';
    case 'Foggy': return '/weather-icons/foggy.png';
    case 'Drizzle': return '/weather-icons/rainy.png';
    case 'Rain': return '/weather-icons/rainy.png';
    case 'Snow': return '/weather-icons/snowy.png';
    case 'Thunderstorm': return '/weather-icons/thunderstorm.png';
    default: return '/weather-icons/cloudy.png';
  }
};

const DriveTimesView: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [trafficAlerts, setTrafficAlerts] = useState<TrafficAlert[]>([]);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mock destinations with route options
  const destinations: Destination[] = [
    {
      name: 'Cleveland Hopkins Airport',
      routes: [
        { via: 'I-90 W to I-71 S', driveTime: 22,
          coordinates: [{lat: 41.45622,lng: -81.70978}, {lat: 41.41165,lng: -81.83430}]},
        { via: 'I-90 W to I-480 W', driveTime: 28,
          coordinates: [{lat: 41.45289, lng: -81.69065}, {lat: 41.41165,lng: -81.83430}]},
      ]
    },
    {
      name: 'Rock & Roll Hall of Fame',
      routes: [
        { via: 'E 9th St', driveTime: 5,
          coordinates: [{}, {}] },
        { via: 'Lakeside Ave', driveTime: 6,
          coordinates: [{}, {}] }
      ]
    },
    {
      name: 'Ohio City',
      routes: [
        { via: 'Detroit Ave', driveTime: 8,
          coordinates: [{}, {}] },
        { via: 'I-90 West', driveTime: 10,
          coordinates: [{}, {}] }
      ]
    },
    {
      name: 'University Circle',
      routes: [
        { via: 'Euclid Ave', driveTime: 14,
          coordinates: [{}, {}] },
        { via: 'Chester Ave', driveTime: 16,
          coordinates: [{}, {}] }
      ]
    },
    {
      name: 'Playhouse Square',
      routes: [
        { via: 'Euclid Ave', driveTime: 4,
          coordinates: [{}, {}] },
        { via: 'Prospect Ave', driveTime: 5,
          coordinates: [{}, {}] }
      ]
    },
    {
      name: 'West Side Market',
      routes: [
        { via: 'W 25th St', driveTime: 7,
          coordinates: [{}, {}] },
        { via: 'Lorain Ave', driveTime: 9,
          coordinates: [{}, {}] }
      ]
    },
  ];


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch weather data from Open-Meteo
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        let data;

        if (config.useMocks) {
          // Use mock data
          data = mockWeatherData;
        } else {
          // Fetch real data
          const response = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${STADIUM_LAT}&longitude=${STADIUM_LNG}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&hourly=temperature_2m,weather_code&temperature_unit=fahrenheit&wind_speed_unit=mph&forecast_days=1&timezone=America%2FNew_York`
          );
          data = await response.json();
        }

        // Get current hour index for hourly data
        const now = new Date();
        const currentHour = now.getHours();

        // Build hourly forecast (next 6 hours)
        const hourlyForecast: HourlyForecast[] = [];
        for (let i = 0; i < 6; i++) {
          const hourIndex = currentHour + i;
          if (hourIndex < data.hourly.time.length) {
            const hourDate = new Date(data.hourly.time[hourIndex]);
            const hourLabel = i === 0 ? 'NOW' : hourDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              hour12: true
            });
            hourlyForecast.push({
              hour: hourLabel,
              temp: Math.round(data.hourly.temperature_2m[hourIndex]),
              condition: getWeatherCondition(data.hourly.weather_code[hourIndex])
            });
          }
        }

        setWeather({
          temperature: Math.round(data.current.temperature_2m),
          condition: getWeatherCondition(data.current.weather_code),
          humidity: Math.round(data.current.relative_humidity_2m),
          windSpeed: Math.round(data.current.wind_speed_10m),
          hourly: hourlyForecast
        });
      } catch (error) {
        console.error('Error fetching weather:', error);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, config.dataRefreshInterval);

    return () => clearInterval(interval);
  }, []);

  // Fetch traffic alerts from HERE API
  useEffect(() => {
    const fetchTrafficAlerts = async () => {
      try {
        let data: HereTrafficResponse;

        if (config.useMocks) {
          // Use mock data
          data = mockTrafficData;
        } else {
          // Fetch real data
          const response = await fetch(
            `https://data.traffic.hereapi.com/v7/incidents?locationReferencing=shape&in=circle:41.50,-81.68;r=20000&apiKey=${config.here.apiKey}`
          );
          data = await response.json();
        }
        console.log(data);

        const alerts: TrafficAlert[] = data.results.map(incident => {
          // Get first point of the incident to calculate distance
          const firstPoint = incident.location.shape.links[0]?.points[0];
          const lat = firstPoint?.lat || 0;
          const lng = firstPoint?.lng || 0;
          const distance = calculateDistance(STADIUM_LAT, STADIUM_LNG, lat, lng);

          let type: TrafficAlert['type'] = 'other';
          switch (incident.incidentDetails.type) {
            case 'construction':
              type = 'construction';
              break;
            case 'roadClosure':
              type = 'closure';
              break;
            case 'accident':
              type = 'accident';
              break;
            case 'congestion':
            case 'disabledVehicle':
              type = 'delay';
              break;
            default:
              type = 'other';
          }

          return {
            id: incident.incidentDetails.id,
            type,
            location: incident.incidentDetails.description.value,
            description: incident.incidentDetails.summary.value,
            criticality: incident.incidentDetails.criticality,
            distance
          };
        });

        // Sort: road names first (TEMPORARY), then critical, then by distance
        alerts.sort((a, b) => {
          // TEMPORARY: Prioritize alerts with actual road names
          const aHasRoad = hasRoadName(a.location);
          const bHasRoad = hasRoadName(b.location);
          if (aHasRoad && !bHasRoad) return -1;
          if (!aHasRoad && bHasRoad) return 1;

          // Critical alerts first
          if (a.criticality === 'critical' && b.criticality !== 'critical') return -1;
          if (a.criticality !== 'critical' && b.criticality === 'critical') return 1;
          // Then by distance
          return a.distance - b.distance;
        });

        // Only top 6
        setTrafficAlerts(alerts.slice(0, 6));
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Error fetching traffic alerts:', error);
      }
    };

    fetchTrafficAlerts();
    const interval = setInterval(fetchTrafficAlerts, config.dataRefreshInterval);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return '';
    return `Updated ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  };

  const getAlertTypeLabel = (type: TrafficAlert['type']): string => {
    switch (type) {
      case 'accident': return 'ACCIDENT';
      case 'construction': return 'CONSTRUCTION';
      case 'closure': return 'CLOSURE';
      case 'delay': return 'DELAY';
      default: return 'ALERT';
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img
          src="/cavs-logo-2.png"
          alt="Cleveland Cavaliers"
          style={styles.logo}
        />
        <div style={styles.headerContent}>
          <h1 style={styles.headerTitle}>Drive Times & Traffic</h1>
          {!isMobile && (
            <div style={styles.dateTime}>
              <span style={styles.time}>{formatTime(currentTime)}</span>
              <span style={styles.date}>{formatDate(currentTime)}</span>
            </div>
          )}
        </div>
      </div>

      <div style={styles.content}>
        {/* Main content area */}
        <div style={isMobile ? styles.mainGridMobile : styles.mainGrid}>
          {/* Drive Times Section */}
          <div style={styles.driveTimesSection}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Drive Times from Rocket Mortgage FieldHouse</h2>
            </div>
            <div style={isMobile ? styles.destinationsGridMobile : styles.destinationsGrid}>
              {destinations.map((dest, index) => (
                <div key={index} style={styles.destinationCard}>
                  <div style={styles.destName}>{dest.name}</div>
                  <div style={styles.routesList}>
                    {dest.routes.map((route, routeIndex) => (
                      <div key={routeIndex} style={styles.routeRow}>
                        <div style={styles.routeVia}>via {route.via}</div>
                        <div style={styles.routeTime}>
                          <span style={styles.timeValue}>{route.driveTime}</span>
                          <span style={styles.timeUnit}>min</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column */}
          <div style={styles.rightColumn}>
            {/* Weather Section */}
            <div style={styles.weatherSection}>
              {weather ? (
                <>
                  <div style={styles.weatherCurrent}>
                    <div style={styles.tempLarge}>{weather.temperature}°F</div>
                    <div style={styles.weatherInfo}>
                      <div style={styles.weatherCondition}>{weather.condition}</div>
                      <div style={styles.weatherStats}>
                        <span>Humidity: {weather.humidity}%</span>
                        <span>Wind: {weather.windSpeed} mph</span>
                      </div>
                    </div>
                  </div>
                  <div style={styles.hourlyForecast}>
                    {weather.hourly.map((hour, index) => (
                      <div key={index} style={styles.hourlyItem}>
                        <div style={styles.hourlyTime}>{hour.hour}</div>
                        <img src={getWeatherIcon(hour.condition)} alt={hour.condition} style={styles.hourlyIcon} />
                        <div style={styles.hourlyTemp}>{hour.temp}°</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={styles.weatherLoading}>Loading weather...</div>
              )}
            </div>

            {/* Traffic Alerts Section */}
            <div style={styles.alertsSection}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>Traffic Alerts</h2>
              </div>
              <div style={styles.alertsList}>
                {trafficAlerts.map(alert => (
                  <div key={alert.id} style={styles.alertItem}>
                    <div style={styles.alertType}>{getAlertTypeLabel(alert.type)}</div>
                    <div style={styles.alertLocation}>{alert.location}</div>
                    <div style={styles.alertDescription}>{alert.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {!isMobile && (
          <div style={styles.footer}>
            <div style={styles.realtimeBadge}>
              <span style={styles.liveDot}></span>
              LIVE UPDATES
            </div>
            {lastUpdated && <div style={styles.footerUpdated}>{formatLastUpdated(lastUpdated)}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    height: '100vh',
    backgroundColor: '#000000',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    backgroundColor: '#860038',
    padding: '1vh 2vw',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    gap: '1vw',
    borderBottom: '2px solid #fdbb30',
    minHeight: '8vh',
    flexShrink: 0
  },
  logo: {
    height: '7vh',
    maxHeight: '80px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))'
  },
  headerContent: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minWidth: 0
  },
  headerTitle: {
    color: '#fdbb30',
    fontSize: 'clamp(18px, 2vw, 28px)',
    fontWeight: '900',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
  },
  dateTime: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexShrink: 0
  },
  time: {
    color: '#fdbb30',
    fontSize: 'clamp(16px, 1.8vw, 26px)',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },
  date: {
    color: 'rgba(253, 187, 48, 0.8)',
    fontSize: 'clamp(11px, 0.9vw, 13px)',
    fontWeight: '500',
    whiteSpace: 'nowrap'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 'clamp(15px, 2vh, 30px) clamp(12px, 1.8vw, 24px)',
    background: 'linear-gradient(135deg, black 0%, #860038 100%)',
    overflow: 'hidden',
    minHeight: 0
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr',
    gap: 'clamp(10px, 1.2vh, 14px)',
    flex: 1,
    minHeight: 0
  },
  mainGridMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    flex: 1,
    minHeight: 0
  },
  driveTimesSection: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 'clamp(6px, 0.8vh, 8px)',
    border: '1px solid #fdbb30',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0
  },
  sectionHeader: {
    backgroundColor: '#000',
    padding: 'clamp(6px, 0.8vh, 8px) clamp(8px, 0.8vw, 9px)',
    borderBottom: '1px solid #fdbb30',
    flexShrink: 0
  },
  sectionTitle: {
    color: '#fdbb30',
    fontSize: 'clamp(12px, 1vw, 14px)',
    fontWeight: '800',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  destinationsGrid: {
    flex: 1,
    padding: 'clamp(8px, 1vh, 10px)',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'clamp(6px, 0.8vh, 8px)',
    overflow: 'auto',
    minHeight: 0
  },
  destinationsGridMobile: {
    flex: 1,
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    overflow: 'auto',
    minHeight: 0
  },
  destinationCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 'clamp(4px, 0.6vh, 6px)',
    padding: 'clamp(8px, 1vh, 10px)',
    border: '1px solid #fdbb30'
  },
  destName: {
    fontSize: 'clamp(12px, 1vw, 14px)',
    fontWeight: '800',
    color: '#860038',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    marginBottom: 'clamp(8px, 1vh, 10px)',
    borderBottom: '1px solid rgba(134, 0, 56, 0.2)',
    paddingBottom: 'clamp(6px, 0.8vh, 8px)'
  },
  routesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(3px, 0.4vh, 4px)'
  },
  routeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'clamp(4px, 0.6vh, 6px)'
  },
  routeVia: {
    fontSize: 'clamp(12px, 1vw, 14px)',
    fontWeight: '500',
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
    paddingRight: '8px'
  },
  routeTime: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 'clamp(6px, 0.6vw, 8px)',
    flexShrink: 0
  },
  timeValue: {
    fontSize: 'clamp(16px, 1.5vw, 20px)',
    fontWeight: '900',
    color: '#860038'
  },
  timeUnit: {
    fontSize: 'clamp(11px, 1vw, 13px)',
    fontWeight: '600',
    color: '#666'
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(10px, 1.2vh, 14px)',
    minHeight: 0
  },
  weatherSection: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 'clamp(6px, 0.8vh, 8px)',
    border: '1px solid #fdbb30',
    padding: 'clamp(5px, 0.6vh, 6px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(8px, 1vh, 10px)',
    flexShrink: 0
  },
  weatherLoading: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    textAlign: 'center',
    padding: 'clamp(15px, 2vh, 20px)'
  },
  weatherCurrent: {
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(12px, 1.2vw, 16px)'
  },
  tempLarge: {
    fontSize: 'clamp(22px, 2.2vw, 31px)',
    fontWeight: '900',
    color: '#fdbb30',
    paddingLeft: 'clamp(6px, 0.6vw, 8px)'
  },
  weatherInfo: {
    flex: 1,
    minWidth: 0
  },
  weatherCondition: {
    fontSize: 'clamp(12px, 1.2vw, 15px)',
    fontWeight: '700',
    color: '#fff',
    marginBottom: '2px'
  },
  weatherStats: {
    display: 'flex',
    gap: 'clamp(8px, 0.8vw, 10px)',
    fontSize: 'clamp(10px, 0.9vw, 12px)',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  hourlyForecast: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: '1px solid rgba(253, 187, 48, 0.3)',
    paddingTop: 'clamp(8px, 1vh, 10px)'
  },
  hourlyItem: {
    textAlign: 'center',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  hourlyTime: {
    fontSize: 'clamp(10px, 0.9vw, 12px)',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: '600',
    marginBottom: 'clamp(3px, 0.4vh, 4px)',
    whiteSpace: 'nowrap'
  },
  hourlyIcon: {
    width: 'clamp(14px, 1.4vw, 18px)',
    height: 'clamp(14px, 1.4vw, 18px)',
    marginBottom: '2px',
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)'
  },
  hourlyTemp: {
    fontSize: 'clamp(13px, 1.2vw, 16px)',
    fontWeight: '700',
    color: '#fdbb30'
  },
  alertsSection: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 'clamp(6px, 0.8vh, 8px)',
    border: '1px solid #fdbb30',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0
  },
  alertsList: {
    flex: 1,
    padding: 'clamp(6px, 0.8vh, 8px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'clamp(6px, 0.8vh, 8px)',
    overflow: 'auto',
    minHeight: 0
  },
  alertItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 'clamp(3px, 0.4vh, 4px)',
    padding: 'clamp(6px, 0.8vh, 8px) clamp(10px, 1vw, 12px)'
  },
  alertType: {
    fontSize: 'clamp(10px, 0.9vw, 12px)',
    fontWeight: '800',
    color: '#860038',
    letterSpacing: '0.3px',
    marginBottom: '2px'
  },
  alertLocation: {
    fontSize: 'clamp(11px, 1vw, 13px)',
    fontWeight: '700',
    color: '#000',
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  alertDescription: {
    fontSize: 'clamp(10px, 0.9vw, 12px)',
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
  },
  footer: {
    textAlign: 'center',
    paddingTop: 'clamp(10px, 1.5vh, 20px)',
    flexShrink: 0
  },
  footerUpdated: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 'clamp(11px, 1vw, 14px)',
    fontWeight: '500',
    marginTop: 'clamp(4px, 0.5vh, 6px)'
  },
  realtimeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'clamp(8px, 1vw, 12px)',
    backgroundColor: '#860038',
    color: '#fdbb30',
    padding: 'clamp(8px, 1vh, 12px) clamp(16px, 1.8vw, 24px)',
    borderRadius: 'clamp(15px, 1.5vh, 20px)',
    fontSize: 'clamp(12px, 1.2vw, 16px)',
    fontWeight: '900',
    letterSpacing: '1px',
    boxShadow: '0 4px 12px rgba(134, 0, 56, 0.4)',
    border: '1px solid #fdbb30'
  },
  liveDot: {
    width: 'clamp(8px, 1vw, 12px)',
    height: 'clamp(8px, 1vw, 12px)',
    backgroundColor: '#fdbb30',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'pulse 2s ease-in-out infinite',
    boxShadow: '0 0 8px #fdbb30'
  }
};

export default DriveTimesView;
